// ─── Bracket layout constants (match Flutter bracket_screen.dart) ────────────
export const CARD_H   = 72;  // px — card height
export const GAP_V    = 16;  // px — vertical gap between round-0 cards
export const CARD_W   = 200; // px — card width
export const COL_GAP  = 48;  // px — horizontal gap between columns
export const LABEL_H  = 36;  // px — space reserved for round label

// ─── Pure helpers ────────────────────────────────────────────────────────────

export function isDoubleBye(match) {
  // A true double-bye is a completed match with no real players — not just an empty placeholder
  return !match.player1Name && !match.player2Name &&
    match.status === 'complete' && !match.__virtualBye;
}

/**
 * Group matches by round number, sort within each round by position,
 * and fill gaps with virtual bye entries so positions are sequential.
 * Also fills trailing positions so later rounds have the correct number
 * of slots based on the first round count (e.g. 4 QFs → 2 SFs → 1 Final).
 *
 * Returns { rounds, sortedRounds, sortedMatches }
 */
export function buildRoundsWithByes(matches) {
  // Group actual matches by round
  const byRound = {};
  for (const m of matches) {
    const r = m.round ?? 0;
    if (!byRound[r]) byRound[r] = {};
    byRound[r][m.position ?? 0] = m;
  }

  // Determine round 0 count from actual data
  const r0Existing = byRound[0] ? Object.keys(byRound[0]).map(Number) : [];
  const r0Count = r0Existing.length > 0 ? Math.max(...r0Existing) + 1 : 1;

  // Calculate total rounds needed for the full bracket
  let totalRounds = 1;
  let slots = r0Count;
  while (slots > 1) {
    slots = Math.ceil(slots / 2);
    totalRounds++;
  }

  // Build all rounds with placeholders for missing matches
  const sortedRounds = [];
  const sortedMatches = {};
  let matchesInRound = r0Count;

  for (let r = 0; r < totalRounds; r++) {
    sortedRounds.push(r);
    const existing = byRound[r] || {};
    const filled = [];

    for (let pos = 0; pos < matchesInRound; pos++) {
      if (existing[pos]) {
        filled.push(existing[pos]);
      } else {
        filled.push({
          matchId: `__placeholder_${r}_${pos}`,
          position: pos,
          round: r,
          player1Name: null,
          player2Name: null,
          status: 'pending',
          score1: null,
          score2: null,
          winner: null,
          __virtualBye: true,
        });
      }
    }
    sortedMatches[r] = filled;
    matchesInRound = Math.max(1, Math.ceil(matchesInRound / 2));
  }

  return { rounds: byRound, sortedRounds, sortedMatches };
}

/**
 * Top Y position for a card (Flutter algorithm).
 */
export function cardTop(roundIdx, position, slotH0, cardH) {
  const span = Math.pow(2, roundIdx);
  const spanH = span * slotH0;
  return position * spanH + (spanH - cardH) / 2;
}

/**
 * Center Y of a card (for connector lines).
 */
export function cardCenterY(roundIdx, position, slotH0, cardH) {
  return cardTop(roundIdx, position, slotH0, cardH) + cardH / 2;
}

/**
 * Build connector line data (pure data, no JSX).
 *
 * Returns an array of objects:
 *   { type: 'straight', x1, y1, x2, y2 }
 *   { type: 'bracket', rightEdge, midX, nextColLeft, f1CY, f2CY, targetCY }
 */
export function buildConnectorLines(sortedRounds, sortedMatches, {
  cardW = CARD_W,
  colGap = COL_GAP,
  cardH = CARD_H,
  gapV = GAP_V,
  labelH = LABEL_H,
} = {}) {
  const slotH0 = cardH + gapV;
  const totalRounds = sortedRounds.length;
  const lines = [];

  for (let idx = 0; idx < totalRounds - 1; idx++) {
    const round = sortedRounds[idx];
    const nextRound = sortedRounds[idx + 1];
    const curMatches = sortedMatches[round];
    const nextMatches = sortedMatches[nextRound];

    const colLeft = idx * (cardW + colGap);
    const rightEdge = colLeft + cardW;
    const nextColLeft = (idx + 1) * (cardW + colGap);
    const midX = rightEdge + colGap / 2;

    for (let ni = 0; ni < nextMatches.length; ni++) {
      const feeder1Idx = ni * 2;
      const feeder2Idx = ni * 2 + 1;

      if (feeder1Idx >= curMatches.length) continue;

      const feeder1 = curMatches[feeder1Idx];
      const feeder2 = feeder2Idx < curMatches.length ? curMatches[feeder2Idx] : null;
      const target = nextMatches[ni];
      const targetCY = cardCenterY(idx + 1, target.position ?? ni, slotH0, cardH) + labelH;

      const f1Bye = isDoubleBye(feeder1);
      const f2Bye = feeder2 ? isDoubleBye(feeder2) : true;

      if (f1Bye && f2Bye) continue;

      const f1CY = cardCenterY(idx, feeder1.position ?? feeder1Idx, slotH0, cardH) + labelH;
      const f2CY = feeder2 ? cardCenterY(idx, feeder2.position ?? feeder2Idx, slotH0, cardH) + labelH : null;

      if (f1Bye || f2Bye) {
        const realCY = f1Bye ? f2CY : f1CY;
        lines.push({
          type: 'straight',
          x1: rightEdge,
          y1: realCY,
          x2: nextColLeft,
          y2: targetCY,
        });
      } else {
        lines.push({
          type: 'bracket',
          rightEdge,
          midX,
          nextColLeft,
          f1CY,
          f2CY,
          targetCY,
        });
      }
    }
  }

  return lines;
}

/**
 * Apply a score_resolved WebSocket event to a matches array (immutable).
 * Returns the new matches array, including advancing the winner name
 * into the next-round match.
 */
export function applyScoreResolved(matches, event) {
  if (event.resolution !== 'approved') {
    // Rejected — clear the pending score indicator
    return matches.map(m =>
      m.matchId === event.matchId
        ? { ...m, hasPendingScore: false, pendingScore1: null, pendingScore2: null }
        : m
    );
  }

  // Find the match that was resolved
  const resolvedMatch = matches.find(m => m.matchId === event.matchId);
  const matchStatus = event.matchStatus ?? 'complete'; // legacy events default to complete

  return matches.map(m => {
    // Update the resolved match itself
    if (m.matchId === event.matchId) {
      return {
        ...m,
        score1: event.score1,
        score2: event.score2,
        winner: event.winner ?? null,
        status: matchStatus,
        hasPendingScore: false,
        pendingScore1: null,
        pendingScore2: null,
      };
    }

    // Advance winner name into the next-round match — only when match is complete
    if (resolvedMatch && matchStatus === 'complete' && event.winner) {
      const nextRound = resolvedMatch.round + 1;
      const nextPosition = Math.floor(resolvedMatch.position / 2);
      if (m.round === nextRound && m.position === nextPosition) {
        const winnerName = event.winner === 'player1'
          ? resolvedMatch.player1Name
          : resolvedMatch.player2Name;
        // Even position → player1 slot, odd → player2 slot
        if (resolvedMatch.position % 2 === 0) {
          return { ...m, player1Name: winnerName };
        } else {
          return { ...m, player2Name: winnerName };
        }
      }
    }

    return m;
  });
}
